import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2, UserPlus } from 'lucide-react';
import type {} from '@malgoos/shared';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Estado, Vazio } from '../../componentes/estados';
import { BuscaRemota, Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { BASE, BotaoIcone } from './comum';
import { duracaoTexto, limpar, type Curso, type Trilha, type TrilhaFicha } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** M11.5: trilhas de cursos, com atribuição automática por função na admissão. */
export function Trilhas() {
  const { t } = useTranslation('treinamento');
  const s = useSessao();
  const [aberta, setAberta] = useState<Trilha | 'nova' | null>(null);
  const podeEditar = s.pode('treinamento.editar');
  // Val5 (29/10): criar trilha pede `treinamento.criar`.
  const podeCriar = s.pode('treinamento.criar');

  const colunas: Coluna<Trilha>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (x) => <span className="font-medium">{x.nome}</span> },
    { chave: 'funcao_nome', rotulo: t('campos.funcao'), render: (x) => x.funcao_nome ?? <span className="text-tinta-3">{t('trilhas.semFuncao')}</span> },
    { chave: 'cursos', rotulo: t('trilhas.cursos'), alinhar: 'direita', mono: true },
    { chave: 'duracao_min', rotulo: t('trilhas.duracao'), alinhar: 'direita', mono: true, render: (x) => duracaoTexto(x.duracao_min) },
    { chave: 'matriculas', rotulo: t('trilhas.matriculas'), alinhar: 'direita', mono: true },
    { chave: 'descricao', rotulo: t('campos.descricao'), oculta: true },
  ];

  return (
    <>
      {(podeCriar || s.pode('treinamento.excluir')) && (
        <AcaoPrimaria>
          {s.pode('treinamento.excluir') && <BotaoLixeira entidades={['treinamento.trilha']} />}
          {podeCriar && (
            <>
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberta('nova')}>
                {t('acoes.novaTrilha')}
              </Botao>
            </>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Trilha> tela="treinamento.trilhas" caminho="/treinamento/trilhas" chaveConsulta={['treinamento', 'trilhas']} colunas={colunas} aoClicar={(x) => setAberta(x)} vazio={<Vazio texto={podeEditar ? t('vazio.trilhasGestor') : t('vazio.trilhas')} />} />
      {aberta && <PainelTrilha trilha={aberta === 'nova' ? null : aberta} aoFechar={() => setAberta(null)} />}
    </>
  );
}

function PainelTrilha({ trilha, aoFechar }: { trilha: Trilha | null; aoFechar: () => void }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const podeEditar = s.pode('treinamento.editar');
  const [remover, setRemover] = useState(false);
  const [excluida, setExcluida] = useState(false);
  const q = useQuery({ queryKey: ['treinamento', 'trilha', trilha?.id], queryFn: () => get<TrilhaFicha>(`/treinamento/trilhas/${trilha!.id}`), enabled: !!trilha && !excluida });
  const invalidar = () => qc.invalidateQueries({ queryKey: ['treinamento'] });

  const excluir = useMutation({
    mutationFn: () => del(`/treinamento/trilhas/${trilha!.id}`),
    onSuccess: async () => {
      setRemover(false);
      setExcluida(true);
      qc.removeQueries({ queryKey: ['treinamento', 'trilha', trilha!.id] });
      aoFechar();
      toast.sucesso(tn('comum.removido'));
      await invalidar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={trilha ? trilha.nome : t('acoes.novaTrilha')}
      rodape={
        (trilha ? podeEditar : s.pode('treinamento.criar')) ? (
          <>
            {trilha && s.pode('treinamento.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <>
              <Botao variante="fantasma" onClick={aoFechar}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-trilha">
                {tn('comum.salvar')}
              </Botao>
            </>
          </>
        ) : undefined
      }
    >
      {trilha ? (
        <Estado consulta={q}>
          {(d) =>
            podeEditar ? (
              <Editor ficha={d} aoSalvar={aoFechar} invalidar={invalidar} />
            ) : (
              <Leitura ficha={d} abrir={(id) => navegar(`${BASE}/curso/${id}`)} />
            )
          }
        </Estado>
      ) : (
        <Editor ficha={null} aoSalvar={aoFechar} invalidar={invalidar} />
      )}
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluirTrilha')} texto={t('confirmar.excluirTrilhaTexto')} perigoso carregando={excluir.isPending} />
    </Drawer>
  );
}

function Leitura({ ficha, abrir }: { ficha: TrilhaFicha; abrir: (id: string) => void }) {
  const { t } = useTranslation('treinamento');
  return (
    <div className="flex flex-col gap-4">
      {ficha.descricao && <p className="text-sm text-tinta-2">{ficha.descricao}</p>}
      <p className="text-xs text-tinta-3">{ficha.funcao_nome ? t('trilhas.paraFuncao', { funcao: ficha.funcao_nome }) : t('trilhas.semFuncao')}</p>
      <ol className="divide-y divide-borda rounded-lg border border-borda">
        {ficha.itens.map((c, i) => (
          <li key={c.id}>
            <button type="button" onClick={() => abrir(c.id)} className="flex w-full min-w-0 items-center justify-between gap-3 px-3 py-3 text-left text-sm hover:bg-sup-2">
              <TextoCortado className="min-w-0">
                <span className="numero mr-2 text-xs text-tinta-3">{i + 1}</span>
                {c.titulo}
              </TextoCortado>
              {c.minha?.concluida_em ? <Selo cor="verde">{t('selos.concluido')}</Selo> : c.minha ? <Selo cor="azul">{t('selos.emAndamento')}</Selo> : null}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Editor({ ficha, aoSalvar, invalidar }: { ficha: TrilhaFicha | null; aoSalvar: () => void; invalidar: () => Promise<unknown> }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [cursos, setCursos] = useState<{ id: string; titulo: string }[]>(ficha?.itens.map((c) => ({ id: c.id, titulo: c.titulo })) ?? []);
  const [escolhido, setEscolhido] = useState('');
  const [funcionario, setFuncionario] = useState<string | null>(null);
  const podeAtribuir = useSessao().pode('treinamento.atribuir');
  const todos = useQuery({ queryKey: ['treinamento', 'cursos', 'todos'], queryFn: () => getTodos<Curso>('/treinamento/cursos') });
  const disponiveis = (todos.data?.itens ?? []).filter((c) => !cursos.some((x) => x.id === c.id));

  const atribuir = useMutation({
    mutationFn: () => post<{ matriculados: number; ja_matriculados: number; ignorados: number }>(`/treinamento/trilhas/${ficha!.id}/atribuir`, { funcionario_id: funcionario }),
    onSuccess: async (r) => {
      setFuncionario(null);
      await invalidar();
      toast.sucesso(t('trilhas.atribuida', { n: r.matriculados, ja: r.ja_matriculados, ignorados: r.ignorados }));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const mover = (i: number, d: -1 | 1) =>
    setCursos((l) => {
      const n = [...l];
      [n[i], n[i + d]] = [n[i + d]!, n[i]!];
      return n;
    });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'funcao_id', rotulo: t('campos.funcao'), tipo: 'busca', ajuda: t('trilhas.funcaoAjuda'), busca: { caminho: '/pessoas/funcoes', rotulo: (i) => String(i.nome) } },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <Formulario
        key={ficha?.id ?? 'nova'}
        id="form-trilha"
        campos={campos}
        inicial={(ficha ?? {}) as unknown as Record<string, unknown>}
        rotulosAtuais={ficha ? { funcao_id: ficha.funcao_nome ?? '' } : undefined}
        aoEnviar={async (v) => {
          const corpo = { ...limpar({ nome: v.nome, descricao: v.descricao, funcao_id: v.funcao_id }), cursos: cursos.map((c) => c.id) };
          if (ficha) await patch(`/treinamento/trilhas/${ficha.id}`, corpo);
          else await post('/treinamento/trilhas', corpo);
          await invalidar();
          toast.sucesso(tn('comum.salvo'));
          aoSalvar();
        }}
      />

      <section aria-label={t('trilhas.cursosDaTrilha')} className="border-t border-borda pt-4">
        <h3 className="kicker mb-1">{t('trilhas.cursosDaTrilha')}</h3>
        <p className="mb-3 text-xs text-tinta-3">{t('trilhas.cursosAjuda')}</p>
        {cursos.length === 0 ? (
          <p className="mb-3 text-sm text-tinta-3">{t('vazio.trilhaSemCursos')}</p>
        ) : (
          <ol className="mb-3 divide-y divide-borda rounded-lg border border-borda">
            {cursos.map((c, i) => (
              <li key={c.id} className="flex min-w-0 items-center justify-between gap-2 px-3 py-2 text-sm">
                <TextoCortado className="min-w-0">
                  <span className="numero mr-2 text-xs text-tinta-3">{i + 1}</span>
                  {c.titulo}
                </TextoCortado>
                <span className="flex items-center gap-1">
                  <BotaoIcone rotulo={t('acoes.subir')} disabled={i === 0} onClick={() => mover(i, -1)} icone={<ArrowUp className="size-4" />} />
                  <BotaoIcone rotulo={t('acoes.descer')} disabled={i === cursos.length - 1} onClick={() => mover(i, 1)} icone={<ArrowDown className="size-4" />} />
                  <BotaoIcone rotulo={t('acoes.remover')} onClick={() => setCursos((l) => l.filter((x) => x.id !== c.id))} icone={<Trash2 className="size-4" />} perigo />
                </span>
              </li>
            ))}
          </ol>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 basis-56">
            <Selecao aria-label={t('trilhas.adicionarCurso')} value={escolhido} vazio={t('trilhas.adicionarCurso')} onChange={(e) => setEscolhido(e.target.value)} opcoes={disponiveis.map((c) => ({ valor: c.id, rotulo: c.publicado ? c.titulo : `${c.titulo} (${t('selos.rascunho')})` }))} />
          </div>
          <Botao
            icone={<Plus className="size-4" />}
            disabled={!escolhido}
            onClick={() => {
              const c = disponiveis.find((x) => x.id === escolhido);
              if (c) setCursos((l) => [...l, { id: c.id, titulo: c.titulo }]);
              setEscolhido('');
            }}
          >
            {t('acoes.adicionar')}
          </Botao>
        </div>
      </section>

      {/* Val5 (29/10): atribuir a trilha pede `treinamento.atribuir`. */}
      {ficha && podeAtribuir && (
        <section aria-label={t('trilhas.atribuirTitulo')} className="border-t border-borda pt-4">
          <h3 className="kicker mb-1">{t('trilhas.atribuirTitulo')}</h3>
          <p className="mb-3 text-xs text-tinta-3">{t('trilhas.atribuirAjuda')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1 basis-56">
              <BuscaRemota id="atribuir-funcionario" def={{ caminho: '/pessoas/funcionarios', rotulo: (i) => String(i.nome) }} valor={funcionario} aoMudar={(v) => setFuncionario(v)} />
            </div>
            <Botao icone={<UserPlus className="size-4" />} disabled={!funcionario} carregando={atribuir.isPending} onClick={() => atribuir.mutate()}>
              {t('acoes.atribuir')}
            </Botao>
          </div>
        </section>
      )}
    </div>
  );
}
